import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import SearchableSelect from "../Common/SearchableSelect";
import Paggination from "../Paggination";

const ListQtyLayer = () => {
  const { AsyncGetApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [filters, setFilters] = useState({
    accountType: "",
    search: "",
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search !== searchInput) {
          setPage(1);
          return { ...prev, search: searchInput };
        }
        return prev;
      });
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const query = { ...filters, page, limit };
    const result = await AsyncGetApiCall("/trading-settings/quantity/list", query);
    if (result?.success) {
      setData(result.data || []);
      if (result.pagination) {
        setTotalPages(result.pagination.totalPages);
        setTotalItems(result.pagination.total);
      }
    }
    setLoading(false);
  }, [AsyncGetApiCall, filters, page, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchAccountTypes = async () => {
      const result = await AsyncGetApiCall("/trading-settings/account-type");
      if (result?.success) {
        setAccountTypes(result.data || []);
      }
    };
    fetchAccountTypes();
  }, [AsyncGetApiCall]);

  const handleExportCsv = () => {
    if (data.length === 0) return;
    const headers = ["Account Type", "Segment", "Script", "Position Limit", "Min Order", "Max Order", "Value Type"];
    const rows = data.map(item => [
      item.accountType,
      item.segment,
      item.script,
      item.positionLimit,
      item.minOrder,
      item.maxOrder,
      item.valueType
    ]);
    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quantity_settings.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      {loading && <Loader />}
      
      <div className="card radius-16 border-0 shadow-sm mb-24">
        <div className="card-header bg-primary-600 py-16 px-24 d-flex align-items-center justify-content-between position-relative" style={{ overflow: "visible", zIndex: 10 }}>
          <h6 className="text-white mb-0">Show All Qty Settings</h6>
          <div className="d-flex align-items-center gap-3">
            <div style={{ width: "180px", position: "relative", zIndex: 20 }}>
              <input
                type="text"
                className="form-control form-control-sm radius-8"
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div style={{ width: "180px", position: "relative", zIndex: 20 }}>
              <SearchableSelect
                placeholder="Select Account Type"
                value={filters.accountType}
                onChange={(e) => {
                  setFilters({ ...filters, accountType: e.target.value });
                  setPage(1);
                }}
                options={accountTypes.map((type) => ({
                  label: type.name,
                  value: type._id, // use ID
                }))}
              />
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-white radius-8 d-flex align-items-center gap-1"
              onClick={() => {
                setFilters({
                  accountType: "",
                  search: "",
                });
                setSearchInput("");
                setPage(1);
              }}
              title="Reset Filters"
            >
              <Icon icon="lucide:rotate-ccw" />
            </button>
             <button 
               className="btn btn-sm btn-success radius-8 px-16 py-8 d-flex align-items-center gap-2"
               onClick={handleExportCsv}
             >
               <Icon icon="lucide:download" />
               EXPORT CSV
             </button>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table bordered-table mb-0 sm-table">
              <thead>
                <tr>
                  <th>ACCOUNT TYPE</th>
                  <th>SEGMENT</th>
                  <th>SCRIPT</th>
                  <th>POSITION LIMIT</th>
                  <th>MIN ORDER</th>
                  <th>MAX ORDER</th>
                  <th>VALUE TYPE</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item._id}>
                    <td className="text-secondary-light">{item.accountType?.name || item.accountType}</td>
                    <td className="text-secondary-light">{item.segment}</td>
                    <td className="text-secondary-light font-bold">{item.script}</td>
                    <td className="text-secondary-light">{item.positionLimit}</td>
                    <td className="text-secondary-light">{item.minOrder}</td>
                    <td className="text-secondary-light">{item.maxOrder}</td>
                    <td className="text-secondary-light">{item.valueType}</td>
                  </tr>
                ))}
                {data.length === 0 && !loading && (
                  <tr>
                    <td colSpan="7" className="text-center py-5">No settings available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Footer */}
          {totalItems > 0 && (
            <div className="card-footer bg-white py-12 px-16 d-flex flex-column gap-3 border-top mt-16">
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary-light text-sm">Rows per page:</span>
                <select
                  className="form-select form-select-sm radius-8"
                  style={{ width: "70px" }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
              <Paggination
                 total={totalItems}
                 totalPages={totalPages}
                 currentPage={page}
                 setPageNo={setPage}
                 startFrom={(page - 1) * limit + 1}
                 endTo={Math.min(page * limit, totalItems)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ListQtyLayer;
